/**
 * Notification Service
 *
 * API calls for notification management.
 */

import { api } from '@/lib/api';
import type {
  Notification,
  NotificationType,
  NotificationCategory,
  NotificationFilters,
  NotificationStats,
  NotificationPreferences,
} from '../types';
import { stripHtml } from '../utils/stripHtml';
import { deriveActionUrl } from '../utils/deriveActionUrl';

const BASE_URL = '/notifications';

// =============================================================================
// RESPONSE MAPPING
// =============================================================================

/**
 * Backend notification response shape (Prisma model field names).
 * The REST API returns `message` and `createdAt` — the frontend Notification
 * type uses `body` and `timestamp` to match the WebSocket payload convention.
 */
interface BackendNotification {
  id: string;
  type: string;
  category: string;
  title: string;
  message: string;
  data: Record<string, unknown>;
  read: boolean;
  readAt: string | null;
  createdAt: string;
}

interface BackendNotificationPreferences {
  id: string | null;
  emailEnabled: boolean;
  inAppEnabled: boolean;
  smsEnabled: boolean;
  mutedUntil: string | null;
  categories: Record<string, Record<string, boolean>>;
}

interface BackendNotificationPreferencesUpdate {
  emailEnabled?: boolean;
  inAppEnabled?: boolean;
  smsEnabled?: boolean;
  mutedUntil?: string | null;
  categories?: Record<string, Record<string, boolean>>;
}

function mapNotification(raw: BackendNotification): Notification {
  const data = raw.data ?? {};
  const actionLabel = data['actionLabel'];
  return {
    id: raw.id,
    type: raw.type as NotificationType,
    category: raw.category as NotificationCategory,
    title: stripHtml(raw.title),
    body: stripHtml(raw.message),
    data,
    timestamp: raw.createdAt,
    read: raw.read ?? false,
    actionUrl: deriveActionUrl(data),
    actionLabel: typeof actionLabel === 'string' ? stripHtml(actionLabel) : undefined,
  };
}

function getBooleanPreference(
  preferences: Record<string, boolean> | undefined,
  keys: readonly string[],
  fallback: boolean,
): boolean {
  if (!preferences) return fallback;
  for (const key of keys) {
    if (typeof preferences[key] === 'boolean') {
      return preferences[key];
    }
  }
  return fallback;
}

function mapPreferences(raw: BackendNotificationPreferences): NotificationPreferences {
  const categories = Object.entries(raw.categories ?? {}).reduce<
    NotificationPreferences['categories']
  >((acc, [category, prefs]) => {
    acc[category as NotificationCategory] = {
      enabled: getBooleanPreference(prefs, ['enabled'], true),
      email: getBooleanPreference(prefs, ['EMAIL', 'email'], true),
      inApp: getBooleanPreference(prefs, ['IN_APP', 'inApp', 'in_app'], true),
      sms: getBooleanPreference(prefs, ['SMS', 'sms'], true),
      push: getBooleanPreference(prefs, ['PUSH', 'push'], false),
    };
    return acc;
  }, {});

  return {
    id: raw.id,
    email: raw.emailEnabled,
    inApp: raw.inAppEnabled,
    sms: raw.smsEnabled,
    push: false,
    mutedUntil: raw.mutedUntil,
    categories,
  };
}

function mapPreferencesUpdate(
  preferences: Partial<NotificationPreferences>,
): BackendNotificationPreferencesUpdate {
  const payload: BackendNotificationPreferencesUpdate = {};
  if (preferences.email !== undefined) payload.emailEnabled = preferences.email;
  if (preferences.inApp !== undefined) payload.inAppEnabled = preferences.inApp;
  if (preferences.sms !== undefined) payload.smsEnabled = preferences.sms;
  if (preferences.mutedUntil !== undefined) payload.mutedUntil = preferences.mutedUntil;
  if (preferences.categories !== undefined) {
    payload.categories = Object.entries(preferences.categories).reduce<
      Record<string, Record<string, boolean>>
    >((acc, [category, prefs]) => {
      if (!prefs) return acc;
      acc[category] = {
        ...(prefs.enabled !== undefined && { enabled: prefs.enabled }),
        ...(prefs.email !== undefined && { EMAIL: prefs.email }),
        ...(prefs.inApp !== undefined && { IN_APP: prefs.inApp }),
        ...(prefs.sms !== undefined && { SMS: prefs.sms }),
        ...(prefs.push !== undefined && { PUSH: prefs.push }),
      };
      return acc;
    }, {});
  }
  return payload;
}

// =============================================================================
// NOTIFICATION CRUD
// =============================================================================

/**
 * Fetch notifications with filters
 */
export async function fetchNotifications(filters?: NotificationFilters): Promise<Notification[]> {
  const params = new URLSearchParams();
  if (filters?.category) params.set('category', filters.category);
  if (filters?.read !== undefined) params.set('read', String(filters.read));
  if (filters?.fromDate) params.set('fromDate', filters.fromDate);
  if (filters?.toDate) params.set('toDate', filters.toDate);
  if (filters?.limit) params.set('limit', String(filters.limit));
  if (filters?.offset) params.set('offset', String(filters.offset));

  const query = params.toString();
  const url = query ? `${BASE_URL}?${query}` : BASE_URL;
  const raw = await api.get<BackendNotification[]>(url);
  return raw.map(mapNotification);
}

/**
 * Fetch a single notification
 */
export async function fetchNotification(id: string): Promise<Notification> {
  const raw = await api.get<BackendNotification>(`${BASE_URL}/${id}`);
  return mapNotification(raw);
}

/**
 * Mark a notification as read
 */
export async function markAsRead(id: string): Promise<void> {
  await api.patch(`${BASE_URL}/${id}/read`);
}

/**
 * Mark all notifications as read
 */
export async function markAllAsRead(): Promise<{ count: number }> {
  return await api.patch<{ count: number }>(`${BASE_URL}/mark-all-read`);
}

/**
 * Dismiss a notification (SAP-aligned soft-delete)
 */
export async function dismissNotification(id: string): Promise<void> {
  await api.patch(`${BASE_URL}/${id}/dismiss`);
}

/**
 * Dismiss all notifications (SAP-aligned soft-delete)
 */
export async function dismissAllNotifications(): Promise<{ count: number }> {
  return await api.patch<{ count: number }>(`${BASE_URL}/dismiss-all`);
}

// =============================================================================
// STATS & COUNTS
// =============================================================================

/**
 * Fetch notification statistics
 */
export async function fetchNotificationStats(): Promise<NotificationStats> {
  return await api.get<NotificationStats>(`${BASE_URL}/stats`);
}

/**
 * Fetch unread count
 */
export async function fetchUnreadCount(): Promise<number> {
  const response = await api.get<{ count: number }>(`${BASE_URL}/unread-count`);
  return response.count;
}

// =============================================================================
// PREFERENCES
// =============================================================================

/**
 * Fetch notification preferences
 */
export async function fetchNotificationPreferences(): Promise<NotificationPreferences> {
  const raw = await api.get<BackendNotificationPreferences>(`${BASE_URL}/preferences`);
  return mapPreferences(raw);
}

/**
 * Update notification preferences
 */
export async function updateNotificationPreferences(
  preferences: Partial<NotificationPreferences>,
): Promise<NotificationPreferences> {
  const raw = await api.put<BackendNotificationPreferences>(
    `${BASE_URL}/preferences`,
    mapPreferencesUpdate(preferences),
  );
  return mapPreferences(raw);
}

// =============================================================================
// PUSH NOTIFICATIONS
// =============================================================================

/**
 * Register push notification subscription
 */
export async function registerPushSubscription(subscription: PushSubscription): Promise<void> {
  await api.post(`${BASE_URL}/push/subscribe`, {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: subscription.toJSON().keys?.p256dh,
      auth: subscription.toJSON().keys?.auth,
    },
  });
}

/**
 * Unregister push notification subscription
 */
export async function unregisterPushSubscription(endpoint: string): Promise<void> {
  await api.post(`${BASE_URL}/push/unsubscribe`, { endpoint });
}

// =============================================================================
// TEST NOTIFICATIONS (Dev only)
// =============================================================================

/**
 * Send a test notification (development only)
 */
export async function sendTestNotification(
  type: 'EMAIL' | 'SMS' | 'IN_APP' | 'PUSH',
): Promise<void> {
  await api.post(`${BASE_URL}/test`, { type });
}

// =============================================================================
// NOTIFICATION TEMPLATES
// =============================================================================

export interface NotificationTemplate {
  id: string;
  key: string;
  name: string;
  description?: string;
  channel: 'EMAIL' | 'SMS' | 'IN_APP' | 'PUSH';
  subject?: string;
  body: string;
  variables: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Fetch notification templates
 */
export async function fetchNotificationTemplates(
  channel?: 'EMAIL' | 'SMS' | 'IN_APP' | 'PUSH',
): Promise<NotificationTemplate[]> {
  const url = channel ? `${BASE_URL}/templates?channel=${channel}` : `${BASE_URL}/templates`;
  return await api.get<NotificationTemplate[]>(url);
}

/**
 * Fetch a single template
 */
export async function fetchNotificationTemplate(id: string): Promise<NotificationTemplate> {
  return await api.get<NotificationTemplate>(`${BASE_URL}/templates/${id}`);
}

/**
 * Update notification template
 */
export async function updateNotificationTemplate(
  id: string,
  input: Partial<NotificationTemplate>,
): Promise<NotificationTemplate> {
  return await api.patch<NotificationTemplate>(`${BASE_URL}/templates/${id}`, input);
}

/**
 * Preview notification template with variables
 */
export async function previewNotificationTemplate(
  id: string,
  variables: Record<string, string>,
): Promise<{ subject?: string; body: string }> {
  return await api.post<{ subject?: string; body: string }>(`${BASE_URL}/templates/${id}/preview`, {
    variables,
  });
}

// =============================================================================
// NOTIFICATION CHANNELS
// =============================================================================

export interface NotificationChannel {
  id: string;
  type: 'EMAIL' | 'SMS' | 'SLACK' | 'TEAMS' | 'WEBHOOK';
  name: string;
  config: Record<string, unknown>;
  isActive: boolean;
  isDefault: boolean;
  createdAt: string;
}

/**
 * Fetch notification channels
 */
export async function fetchNotificationChannels(): Promise<NotificationChannel[]> {
  return await api.get<NotificationChannel[]>(`${BASE_URL}/channels`);
}

/**
 * Create notification channel
 */
export async function createNotificationChannel(
  input: Omit<NotificationChannel, 'id' | 'createdAt'>,
): Promise<NotificationChannel> {
  return await api.post<NotificationChannel>(`${BASE_URL}/channels`, input);
}

/**
 * Update notification channel
 */
export async function updateNotificationChannel(
  id: string,
  input: Partial<NotificationChannel>,
): Promise<NotificationChannel> {
  return await api.patch<NotificationChannel>(`${BASE_URL}/channels/${id}`, input);
}

/**
 * Delete notification channel
 */
export async function deleteNotificationChannel(id: string): Promise<void> {
  await api.delete(`${BASE_URL}/channels/${id}`);
}

/**
 * Test notification channel
 */
export async function testNotificationChannel(
  id: string,
): Promise<{ success: boolean; message: string }> {
  return await api.post<{ success: boolean; message: string }>(`${BASE_URL}/channels/${id}/test`);
}

// =============================================================================
// SCHEDULED NOTIFICATIONS
// =============================================================================

export interface ScheduledNotification {
  id: string;
  templateId: string;
  recipientType: 'USER' | 'ROLE' | 'DEPARTMENT' | 'ALL';
  recipientIds?: string[];
  scheduledAt: string;
  status: 'PENDING' | 'SENT' | 'FAILED' | 'CANCELLED';
  sentAt?: string;
  createdAt: string;
}

/**
 * Fetch scheduled notifications
 */
export async function fetchScheduledNotifications(): Promise<ScheduledNotification[]> {
  return await api.get<ScheduledNotification[]>(`${BASE_URL}/scheduled`);
}

/**
 * Create scheduled notification
 */
export async function createScheduledNotification(
  input: Omit<ScheduledNotification, 'id' | 'status' | 'sentAt' | 'createdAt'>,
): Promise<ScheduledNotification> {
  return await api.post<ScheduledNotification>(`${BASE_URL}/scheduled`, input);
}

/**
 * Cancel scheduled notification
 */
export async function cancelScheduledNotification(id: string): Promise<void> {
  await api.post(`${BASE_URL}/scheduled/${id}/cancel`);
}

// =============================================================================
// BROADCAST NOTIFICATIONS
// =============================================================================

export interface BroadcastInput {
  title: string;
  message: string;
  channel: ('EMAIL' | 'SMS' | 'IN_APP' | 'PUSH')[];
  recipientType: 'ALL' | 'ROLE' | 'DEPARTMENT';
  recipientIds?: string[];
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  scheduledAt?: string;
}

/**
 * Send broadcast notification
 */
export async function sendBroadcast(
  input: BroadcastInput,
): Promise<{ sent: number; failed: number }> {
  return await api.post<{ sent: number; failed: number }>(`${BASE_URL}/broadcast`, input);
}

// =============================================================================
// NOTIFICATION LOGS
// =============================================================================

export interface NotificationLog {
  id: string;
  notificationId: string;
  channel: 'EMAIL' | 'SMS' | 'IN_APP' | 'PUSH';
  recipientId: string;
  status: 'PENDING' | 'SENT' | 'DELIVERED' | 'FAILED' | 'BOUNCED';
  error?: string;
  sentAt?: string;
  deliveredAt?: string;
  createdAt: string;
}

/**
 * Fetch notification logs
 */
export async function fetchNotificationLogs(filters?: {
  status?: string;
  channel?: string;
  fromDate?: string;
  toDate?: string;
  limit?: number;
  offset?: number;
}): Promise<NotificationLog[]> {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.channel) params.set('channel', filters.channel);
  if (filters?.fromDate) params.set('fromDate', filters.fromDate);
  if (filters?.toDate) params.set('toDate', filters.toDate);
  if (filters?.limit) params.set('limit', String(filters.limit));
  if (filters?.offset) params.set('offset', String(filters.offset));

  const query = params.toString();
  const url = query ? `${BASE_URL}/logs?${query}` : `${BASE_URL}/logs`;
  return await api.get<NotificationLog[]>(url);
}

// =============================================================================
// CONSOLIDATED API OBJECT
// =============================================================================

export const notificationApi = {
  // Notifications
  notifications: {
    list: fetchNotifications,
    get: fetchNotification,
    markAsRead,
    markAllAsRead,
    dismiss: dismissNotification,
    dismissAll: dismissAllNotifications,
  },
  // Stats
  stats: {
    get: fetchNotificationStats,
    getUnreadCount: fetchUnreadCount,
  },
  // Preferences
  preferences: {
    get: fetchNotificationPreferences,
    update: updateNotificationPreferences,
  },
  // Push
  push: {
    subscribe: registerPushSubscription,
    unsubscribe: unregisterPushSubscription,
  },
  // Templates
  templates: {
    list: fetchNotificationTemplates,
    get: fetchNotificationTemplate,
    update: updateNotificationTemplate,
    preview: previewNotificationTemplate,
  },
  // Channels
  channels: {
    list: fetchNotificationChannels,
    create: createNotificationChannel,
    update: updateNotificationChannel,
    delete: deleteNotificationChannel,
    test: testNotificationChannel,
  },
  // Scheduled
  scheduled: {
    list: fetchScheduledNotifications,
    create: createScheduledNotification,
    cancel: cancelScheduledNotification,
  },
  // Broadcast
  broadcast: {
    send: sendBroadcast,
  },
  // Logs
  logs: {
    list: fetchNotificationLogs,
  },
  // Test
  test: sendTestNotification,
};
